import {
  FaroErrorBoundary,
  withFaroRouterInstrumentation,
} from "@grafana/faro-react";
import * as Sentry from "@sentry/react";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { FlagProvider, useFlagsStatus } from "@unleash/proxy-client-react";
import * as React from "react";
import { I18nProvider } from "react-aria-components";
import { createRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import {
  type DOMRouterOpts,
  type NavigateOptions,
  type RouteObject,
  createBrowserRouter,
  createHashRouter,
} from "react-router";
import { RouterProvider } from "react-router/dom";
import { StalePayloadError } from "@/shared/bootstrap/bootContract.ts";
import { NavigationBlocker } from "@/shared/queries/networkState.ts";
import type { Router } from "@/shared/types.ts";
import { IntercomHost } from "@/shared/ui/IntercomHost.tsx";
import { SystemMessages } from "@/shared/ui/SystemMessages.tsx";
import { ApplicationError } from "@/shared/ui/errors/500.tsx";
import { TimeoutWatcher } from "@/shared/ui/legacy/TimeoutWatcher.tsx";
import { getCvPartner } from "@/shared/util/getCvPartner.ts";
import { invariant } from "@/shared/util/invariant.ts";
import { QueryClientProvider } from "@/shared/util/queryClient.tsx";
import { i18nextInstance } from "@/shared/util/translations.ts";

declare module "react-aria-components" {
  interface RouterConfig {
    routerOptions: NavigateOptions;
  }
}

const NothingUntilUnleashReady: React.FC<{
  children: React.ReactElement;
}> = ({ children }) => {
  const status = useFlagsStatus();

  if (status.flagsReady || status.flagsError) {
    return children;
  }

  return null;
};

const isDevelopment = process.env.NODE_ENV === "development";

function renderReactRoot(
  children: React.ReactElement,
  selector: string,
  locale: string,
) {
  const element = document.getElementById(selector);
  invariant(element);

  const { unleashConfig, faro } = getCvPartner();

  const root = createRoot(element, {
    onUncaughtError: (error, errorInfo) => {
      faro?.api.pushError(error as Error, {
        context: { componentStack: errorInfo.componentStack ?? "" },
      });
      Sentry.captureException(error, {
        extra: { componentStack: errorInfo.componentStack },
      });
    },
    onCaughtError: (error, errorInfo) => {
      if (error instanceof StalePayloadError) {
        return;
      }

      faro?.api.pushError(error as Error, {
        context: { componentStack: errorInfo.componentStack ?? "" },
      });
      Sentry.captureException(error, {
        level: "warning",
        extra: { componentStack: errorInfo.componentStack },
      });
    },
  });

  root.render(
    <React.StrictMode>
      <FaroErrorBoundary>
        <FlagProvider
          config={unleashConfig}
          startClient={unleashConfig != null}
        >
          <QueryClientProvider>
            <I18nextProvider i18n={i18nextInstance}>
              <I18nProvider locale={locale}>
                <IntercomHost>
                  <SystemMessages />
                  <NothingUntilUnleashReady>
                    <>
                      <TimeoutWatcher />
                      {children}
                    </>
                  </NothingUntilUnleashReady>
                </IntercomHost>
              </I18nProvider>
            </I18nextProvider>
            {isDevelopment && (
              <TanStackDevtools
                config={{ openHotkey: [] }}
                plugins={[
                  {
                    name: "TanStack Query",
                    render: <ReactQueryDevtoolsPanel />,
                  },
                ]}
              />
            )}
          </QueryClientProvider>
        </FlagProvider>
        <NavigationBlocker />
      </FaroErrorBoundary>
    </React.StrictMode>,
  );

  return root;
}

export function createRouter(
  routes: RouteObject[],
  useBrowserHistoryRouter = false,
): Router {
  const opts: DOMRouterOpts = {
    future: {},
  };

  const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(
    useBrowserHistoryRouter ? createBrowserRouter : createHashRouter,
  );

  let router = sentryCreateBrowserRouter(routes, opts);

  const { faro } = getCvPartner();

  if (faro != null) {
    router = withFaroRouterInstrumentation(router);

    router.subscribe(() => {
      const currentHref = window.location.href;

      if (faro.api.getPage()?.url !== currentHref) {
        faro.api.setPage({
          url: currentHref,
          attributes: {
            host: window.location.host,
          },
        });
      }
    });
  }

  return router;
}

export function renderRouter(router: Router, selector: string, locale: string) {
  renderReactRoot(<RouterProvider router={router} />, selector, locale);
}

// Boot failures happen before the router exists, so its errorElement cannot show
// them. Without this the page is blank.
export function renderBootError() {
  const container = document.createElement("div");
  document.body.append(container);

  // Boot may not have got as far as applying the locale, so the one the page was
  // served with is all there is.
  const locale = document.documentElement.lang;

  createRoot(container).render(
    <React.StrictMode>
      <QueryClientProvider>
        <I18nextProvider i18n={i18nextInstance}>
          <I18nProvider locale={locale}>
            <ApplicationError showHeader={false} />
          </I18nProvider>
        </I18nextProvider>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}
